import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button";
import { Plus, GitBranch, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CreatePipelineModal } from "@/components/pipelines-admin/CreatePipelineModalNew";
import { AssignPermissionsModal } from "@/components/pipelines-admin/AssignPermissionsModal";
import { PipelinesTable } from "@/components/pipelines-admin/PipelinesTable";
import { PipelineCard } from "@/components/pipelines-admin/PipelineCard";
import { PipelineFilters } from "@/components/pipelines-admin/PipelineFilters";
import { PipelineDetailsModal } from "@/components/pipelines-admin/PipelineDetailsModal";
import { PipelineCreatedModal } from "@/components/pipelines-admin/PipelineCreatedModal";
import { useToast } from "@/hooks/use-toast";
import { RootState } from "@/store";
import pipelineAPI from "@/services/pipelineService";
import { opportunityAPI } from "@/services/opportunityService";
import { rolesService } from "@/services/roles";
import {
  Pipeline,
  CreatePipelineRequest,
  CreatePipelinePermissionRequest,
  PipelinePermission,
  CreateStageRequest,
  Stage,
} from "@/types/pipeline";
import { Role } from "@/types/roles";

export default function PipelinesAdmin() {
  const { toast } = useToast();
  const company = useSelector((state: RootState) => state.auth.company);
  const companySlug = company?.slug || "";

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<PipelinePermission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pipelineToDelete, setPipelineToDelete] = useState<Pipeline | null>(
    null,
  );
  const [isCreatedModalOpen, setIsCreatedModalOpen] = useState(false);
  const [newlyCreatedPipeline, setNewlyCreatedPipeline] =
    useState<Pipeline | null>(null);
  const [statistics, setStatistics] = useState({
    totalPipelines: 0,
    totalPermissions: 0,
    totalRoles: 0,
  });

  useEffect(() => {
    if (companySlug) {
      loadPipelines();
      loadRoles();
    }
  }, [companySlug]);

  const loadPipelines = async () => {
    try {
      setIsLoading(true);
      const response = await pipelineAPI.getPipelines(companySlug);
      if (response.success) {
        setPipelines(response.data);
      }
    } catch (error: any) {
      console.error("Error al cargar pipelines:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ||
          "No se pudieron cargar los pipelines",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await rolesService.getRoles(companySlug);
      if (response.data) {
        setRoles(response.data);
      }
    } catch (error: any) {
      console.error("Error al cargar roles:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los roles",
        variant: "destructive",
      });
    }
  };

  const loadPermissions = async () => {
    try {
      const allPermissions: PipelinePermission[] = [];
      for (const pipeline of pipelines) {
        const response = await pipelineAPI.getPipelinePermissions(
          companySlug,
          pipeline.id,
        );
        if (response.success) {
          allPermissions.push(...response.data);
        }
      }
      setPermissions(allPermissions);

      setStatistics((prev) => ({
        ...prev,
        totalPermissions: allPermissions.length,
        totalRoles: roles.filter((r) =>
          allPermissions.some((p) => p.role_id === r.id),
        ).length,
      }));
    } catch (error) {
      console.error("Error al cargar permisos:", error);
    }
  };

  useEffect(() => {
    if (pipelines.length > 0) {
      loadPermissions();
    }
  }, [pipelines]);

  const handleCreatePipeline = async (data: {
    pipeline: CreatePipelineRequest;
    permissions: {
      role_id: string;
      can_view: boolean;
      can_edit: boolean;
      can_delete: boolean;
    }[];
    stages?: {
      name: string;
      stage_type: string;
      level: number;
      order_position: number;
      color: string;
    }[];
  }) => {
    try {
      const pipelineResponse = await pipelineAPI.createPipeline(companySlug, {
        ...data.pipeline,
        slug: companySlug,
      } as any);

      if (!pipelineResponse.success) {
        throw new Error("No se pudo crear el pipeline");
      }

      const newPipeline = pipelineResponse.data;

      if (data.permissions.length > 0) {
        const permissionPromises = data.permissions.map((perm) =>
          pipelineAPI.createPipelinePermission(companySlug, {
            pipeline_id: newPipeline.id,
            ...perm,
          }),
        );

        await Promise.all(permissionPromises);
      }

      if (data.stages && data.stages.length > 0) {
        const stagePromises = data.stages.map((stage) =>
          opportunityAPI.createStage({
            slug: companySlug,
            pipeline_id: newPipeline.id,
            name: stage.name,
            stage_type: stage.stage_type,
            order_position: stage.order_position,
            color: stage.color,
            is_active: true,
          }),
        );
        await Promise.all(stagePromises);
      }

      setNewlyCreatedPipeline(newPipeline);
      setIsCreateModalOpen(false);
      setIsCreatedModalOpen(true);

      await loadPipelines();
    } catch (error: any) {
      console.error("Error al crear pipeline:", error);

      let errorMessage = "No se pudo crear el pipeline";

      if (error?.response?.status === 409) {
        errorMessage =
          "Ya existe un pipeline con ese nombre. Por favor, elige otro nombre.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeleteClick = (pipeline: Pipeline) => {
    setPipelineToDelete(pipeline);
    setDeleteDialogOpen(true);
  };

  const handleDeletePipeline = async () => {
    if (!pipelineToDelete) return;

    try {
      await pipelineAPI.deletePipeline(companySlug, pipelineToDelete.id);
      toast({
        title: "Pipeline eliminado",
        description: "El pipeline ha sido eliminado exitosamente",
      });
      loadPipelines();
    } catch (error: any) {
      console.error("Error al eliminar pipeline:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo eliminar el pipeline",
        variant: "destructive",
      });
    } finally {
      setDeleteDialogOpen(false);
      setPipelineToDelete(null);
    }
  };

  const handleAssignPermissions = (pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
    setIsPermissionsModalOpen(true);
  };

  const handleSavePermissions = async (
    data: CreatePipelinePermissionRequest,
  ) => {
    try {
      const response = await pipelineAPI.createPipelinePermission(
        companySlug,
        data,
      );
      if (response.success) {
        toast({
          title: "Permisos asignados",
          description: "Los permisos han sido asignados exitosamente",
        });
        loadPermissions();
        setIsPermissionsModalOpen(false);
        setSelectedPipeline(null);
      }
    } catch (error: any) {
      console.error("Error al asignar permisos:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message ||
          "No se pudieron asignar los permisos",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleDeletePermission = async (permissionId: string) => {
    try {
      const permission = permissions.find((p) => p.id === permissionId);
      if (!permission) return;

      await pipelineAPI.deletePipelinePermission(
        companySlug,
        permission.pipeline_id,
        permissionId,
      );
      toast({
        title: "Permiso eliminado",
        description: "El permiso ha sido eliminado exitosamente",
      });
      loadPermissions();
    } catch (error: any) {
      console.error("Error al eliminar permiso:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "No se pudo eliminar el permiso",
        variant: "destructive",
      });
    }
  };

  const getPipelinePermissionsCount = (pipelineId: string): number => {
    return permissions.filter((p) => p.pipeline_id === pipelineId).length;
  };

  const getExistingRoleIds = (pipelineId: string): string[] => {
    return permissions
      .filter((p) => p.pipeline_id === pipelineId)
      .map((p) => p.role_id);
  };

  const handleViewDetails = async (pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
    setIsDetailsModalOpen(true);
  };

  const filteredPipelines = pipelines.filter((pipeline) => {
    const matchesSearch =
      pipeline.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (pipeline.description || "")
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  useEffect(() => {
    setStatistics({
      totalPipelines: pipelines.length,
      totalPermissions: permissions.length,
      totalRoles: roles.filter((r) =>
        permissions.some((p) => p.role_id === r.id),
      ).length,
    });
  }, [pipelines, permissions, roles]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Gestión de Pipelines
          </h1>
          <p className="text-slate-600">
            Administra pipelines y permisos del sistema
          </p>
        </div>

        <Button
          onClick={() => setIsCreateModalOpen(true)}
          className="bg-black hover:bg-gray-800 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Pipeline
        </Button>
      </div>

      <PipelineFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        statistics={statistics}
      />

      {isLoading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-20 bg-slate-50/50 animate-pulse rounded-lg border border-slate-100" />
          ))}
        </div>
      ) : (
        <>
          <div className="md:hidden space-y-4">
            {filteredPipelines.length === 0 ? (
              <div className="text-center py-12 border rounded-lg">
                <GitBranch className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  No hay pipelines
                </h3>
                <p className="text-slate-600 mb-4">
                  {searchQuery
                    ? "No se encontraron pipelines con los filtros aplicados"
                    : "No hay pipelines creados en el sistema"}
                </p>
                <Button onClick={() => setIsCreateModalOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear primer pipeline
                </Button>
              </div>
            ) : (
              filteredPipelines.map((pipeline) => (
                <PipelineCard
                  key={pipeline.id}
                  pipeline={pipeline}
                  permissionsCount={getPipelinePermissionsCount(pipeline.id)}
                  onAssignPermissions={handleAssignPermissions}
                  onDelete={handleDeleteClick}
                  onViewDetails={handleViewDetails}
                />
              ))
            )}
          </div>

          <div className="hidden md:block">
            <PipelinesTable
              pipelines={filteredPipelines}
              onAssignPermissions={handleAssignPermissions}
              onDelete={(pipelineId) => {
                const pipeline = pipelines.find((p) => p.id === pipelineId);
                if (pipeline) handleDeleteClick(pipeline);
              }}
              onViewDetails={handleViewDetails}
            />
          </div>
        </>
      )}

      <CreatePipelineModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreatePipeline}
        roles={roles}
      />

      {selectedPipeline && (
        <>
          <AssignPermissionsModal
            isOpen={isPermissionsModalOpen}
            onClose={() => {
              setIsPermissionsModalOpen(false);
              setSelectedPipeline(null);
            }}
            onSave={handleSavePermissions}
            pipelineId={selectedPipeline.id}
            pipelineName={selectedPipeline.name}
            roles={roles}
            existingRoleIds={getExistingRoleIds(selectedPipeline.id)}
          />

          <PipelineDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={() => {
              setIsDetailsModalOpen(false);
              setSelectedPipeline(null);
            }}
            pipeline={selectedPipeline}
            onPipelineUpdated={() => {
              loadPipelines();
              loadPermissions();
            }}
          />
        </>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente
              el pipeline "{pipelineToDelete?.name}" y todos sus datos asociados
              (etapas, permisos y oportunidades relacionadas).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePipeline}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PipelineCreatedModal
        isOpen={isCreatedModalOpen}
        onClose={() => {
          setIsCreatedModalOpen(false);
          setNewlyCreatedPipeline(null);
        }}
        pipeline={newlyCreatedPipeline}
        onAddStages={() => {
          if (newlyCreatedPipeline) {
            setIsCreatedModalOpen(false);
            setSelectedPipeline(newlyCreatedPipeline);
            setIsDetailsModalOpen(true);
          }
        }}
      />
    </div>
  );
}
